import React, { useState, useEffect } from 'react';
import styles from '../pages/UserDetailsPage.module.css';
import api from '../utils/api';

const orderStatusOptions = ['Open', 'Pending', 'Closed', 'Cancelled'];
const closeReasonOptions = ['target', 'stoploss', 'manual', 'cancelled', ''];

const Orders = ({ orders, setOrders, onSaveSuccess }) => {
    const [editingOrder, setEditingOrder] = useState(null);
    const [editedOrderData, setEditedOrderData] = useState({});

    useEffect(() => {
        if (editingOrder) {
            const { quantity, openPrice, closePrice, orderStatus, tradeType } = editedOrderData;
            
            if (orderStatus === 'Closed' && openPrice != null && closePrice != null && quantity != null) {
                const pnlMultiplier = (tradeType === 'buy' ? 1 : -1);
                const newPnL = (closePrice - openPrice) * quantity * pnlMultiplier;

                if (newPnL !== editedOrderData.finalPnL) {
                    setEditedOrderData(prevData => ({ ...prevData, finalPnL: newPnL }));
                }
            }
        }
    }, [editedOrderData.quantity, editedOrderData.openPrice, editedOrderData.closePrice, editedOrderData.orderStatus, editingOrder, editedOrderData.finalPnL, editedOrderData.tradeType]);

    const handleEditOrder = (order) => {
        setEditingOrder(order._id);
        const formattedOrderTime = order.orderTime ? new Date(new Date(order.orderTime).getTime() - (new Date().getTimezoneOffset() * 60000)).toISOString().slice(0, 16) : '';
        setEditedOrderData({
            quantity: order.quantity,
            entryPrice: order.entryPrice,
            openPrice: order.openPrice,
            targetPrice: order.targetPrice,
            stoplossPrice: order.stoplossPrice,
            closePrice: order.closePrice,
            orderStatus: order.orderStatus,
            closeReason: order.closeReason || '',
            finalPnL: order.finalPnL,
            positionType: order.positionType || 'Intraday',
            orderTime: formattedOrderTime,
            tradeType: order.tradeType,
        });
    };

    const handleEditedOrderChange = (e) => {
        const { name, value } = e.target;
        setEditedOrderData(prev => ({
            ...prev,
            [name]: (name.includes('Price') || name === 'finalPnL' || name === 'quantity') ? parseFloat(value) : value,
        }));
    };

    const handleSaveOrder = async (orderId) => {
        try {
            const res = await api.put(`/api/admin/orders/${orderId}`, editedOrderData);
            setOrders(orders.map((order) => (order._id === orderId ? res.data : order)));
            setEditingOrder(null);
            setEditedOrderData({});
            alert('Order updated successfully!');
            if (onSaveSuccess) {
                onSaveSuccess();
            }
        } catch (err) {
            console.error('[Admin Frontend] Failed to update order', err);
            alert('Failed to update order');
        }
    };

    const handleCancelEdit = () => {
        setEditingOrder(null);
        setEditedOrderData({});
    };

    const handleDeleteOrder = async (orderId) => {
        if (window.confirm('Are you sure you want to delete this order?')) {
            try {
                await api.delete(`/api/admin/orders/${orderId}`);
                setOrders(orders.filter((order) => order._id !== orderId));
                alert('Order deleted successfully!');
                if (onSaveSuccess) {
                    onSaveSuccess();
                }
            } catch (err) {
                console.error('[Admin Frontend] Failed to delete order', err);
                alert('Failed to delete order');
            }
        }
    };

    return (
        <div className={styles['orders-list']}>
            {orders.length === 0 ? (
                <p>No orders found for this user.</p>
            ) : (
                orders.map((order) => (
                    <div key={order._id} className={styles['order-card']}>
                        <h3>Order ID: {order._id}</h3>
                        {editingOrder === order._id ? (
                            <div className={styles['edit-order-form']}>
                                <div className={styles['form-group']}>
                                    <label>Quantity:</label>
                                    <input type="number" name="quantity" value={editedOrderData.quantity || ''} onChange={handleEditedOrderChange} />
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Entry Price:</label>
                                    <input type="number" name="entryPrice" value={editedOrderData.entryPrice || ''} onChange={handleEditedOrderChange} />
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Open Price:</label>
                                    <input type="number" name="openPrice" value={editedOrderData.openPrice || ''} onChange={handleEditedOrderChange} />
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Target Price:</label>
                                    <input type="number" name="targetPrice" value={editedOrderData.targetPrice || ''} onChange={handleEditedOrderChange} />
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Stoploss Price:</label>
                                    <input type="number" name="stoplossPrice" value={editedOrderData.stoplossPrice || ''} onChange={handleEditedOrderChange} />
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Close Price:</label>
                                    <input type="number" name="closePrice" value={editedOrderData.closePrice || ''} onChange={handleEditedOrderChange} />
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Order Status:</label>
                                    <select name="orderStatus" value={editedOrderData.orderStatus || ''} onChange={handleEditedOrderChange}>
                                        {orderStatusOptions.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Close Reason:</label>
                                    <select name="closeReason" value={editedOrderData.closeReason || ''} onChange={handleEditedOrderChange}>
                                        {closeReasonOptions.map(reason => (
                                            <option key={reason} value={reason}>{reason}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Final PnL:</label>
                                    <input type="number" name="finalPnL" value={editedOrderData.finalPnL || ''} onChange={handleEditedOrderChange} disabled />
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Position Type:</label>
                                    <select name="positionType" value={editedOrderData.positionType || 'Intraday'} onChange={handleEditedOrderChange}>
                                        <option value="Intraday">Intraday</option>
                                        <option value="Carryforward">Carryforward</option>
                                    </select>
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Order Time:</label>
                                    <input type="datetime-local" name="orderTime" value={editedOrderData.orderTime || ''} onChange={handleEditedOrderChange} />
                                </div>
                                <div className={styles['order-actions']}>
                                    <button className={styles['btn-approve']} onClick={() => handleSaveOrder(order._id)}>Save</button>
                                    <button className={styles['delete-button']} onClick={() => handleCancelEdit()}>Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Trade Symbol:</span>
                                    <span>{order.tradeSymbol}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Trade Type:</span>
                                    <span>{order.tradeType}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Order Type:</span>
                                    <span>{order.orderType}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Quantity:</span>
                                    <span>{order.quantity}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Entry Price:</span>
                                    <span>{order.entryPrice}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Target Price:</span>
                                    <span>{order.targetPrice}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Stoploss Price:</span>
                                    <span>{order.stoplossPrice}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Order Status:</span>
                                    <span>{order.orderStatus}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Order Time:</span>
                                    <span>{new Date(order.orderTime).toLocaleString()}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Open Price:</span>
                                    <span>{order.openPrice}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Close Price:</span>
                                    <span>{order.closePrice}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Close Reason:</span>
                                    <span>{order.closeReason}</span>
                                </div>
                                <div className={styles['info-group']}>
                                    <span className={styles['info-label']}>Final PnL:</span>
                                    <span>{order.finalPnL}</span>
                                </div>
                                <div className={styles['order-actions']}>
                                    <button className={styles['btn-approve']} onClick={() => handleEditOrder(order)}>Edit Order</button>
                                    <button onClick={() => handleDeleteOrder(order._id)} className={styles['delete-button']}>Delete Order</button>
                                </div>
                            </>
                        )}
                    </div>
                ))
            )}
        </div>
    );
};

export default Orders;
